// API.jsx
import { createApiClient, get, post, put, del, BASE_URL } from "./ApiBase";
import TokenService from "./tokenService";

// Appende ?admin=<codice> solo se c'e' un codice: stringa vuota e null contano come "niente
// admin" allo stesso modo, e la regola vive in un posto solo invece che ripetuta nei chiamanti.
const withAdmin = (url, codice) => codice ? `${url}?admin=${encodeURIComponent(codice)}` : url;

// Create API clients for each resource type
const API = {
 
  // Authentication methods
  auth: {
    login: async (credentials) => {
      return post(`${BASE_URL}/auth/login`, null, credentials);
    },
    validateToken: async (token) => {
      return get(`${BASE_URL}/auth/validate`, token);
    }
  },
  gTorneo:{
    // l'utente lo prende il backend dal token: non sta piu' nella URL
    mine: async () => {
      return get(`${BASE_URL}/gtorneo/mine`);
    },
    crea: async (dati) => {
      return post(`${BASE_URL}/gtorneo`, null, dati);
    }
  },
  accettazione:{
    edizioni: async () => {
      return get(`${BASE_URL}/accettazione/valid`);
    },
    // Le distinte di tutta l'edizione: per squadra, numero e nome di maglia dei giocatori piu'
    // l'hash che finisce nel QR del referto. E' quello che serve a stampare i referti in bianco,
    // al posto di /accettazione/valid/{id}, che portava anche i loghi in base64 e l'anagrafica.
    distinte: async (idEdizione) => {
      return get(`${BASE_URL}/accettazione/valid/${idEdizione}/distinte`);
    },
    // Le righe della tabella di accettazione: conteggi al posto delle distinte, un booleano al
    // posto del logo (che si chiede a API.squadra.logoUrl).
    riepilogo: async (idEdizione) => {
      return get(`${BASE_URL}/accettazione/valid/${idEdizione}/squadre`);
    },
    // I numeri dei pannelli sotto la tabella: cibo, persone nuove, distribuzioni. Una chiamata
    // sola per tutti e tre, che parte insieme al riepilogo e non lo rallenta.
    analisi: async (idEdizione) => {
      return get(`${BASE_URL}/accettazione/valid/${idEdizione}/analisi`);
    },
    // La distinta di una squadra, da chiedere quando si apre la sua scheda.
    squadra: async (idEdizione, idSquadra) => {
      return get(`${BASE_URL}/accettazione/valid/${idEdizione}/squadra/${idSquadra}`);
    },
    // Il codice che apre la distinta in modifica dal modulo di iscrizione. Gate di scrittura:
    // a chi ha solo lettura risponde 403, ed e' il segnale che il bottone Modifica non va offerto.
    codiceModifica: async (idEdizione, idSquadra) => {
      return get(`${BASE_URL}/accettazione/valid/${idEdizione}/squadra/${idSquadra}/codice-modifica`);
    },
    // Solo id e nome: per le tendine di scelta, senza loghi ne' distinte ne' anagrafica.
    elenco: async (idEdizione) => {
      return get(`${BASE_URL}/accettazione/valid/${idEdizione}/elenco`);
    },
    quota: async (idSquadra, quota) => {
      return post(`${BASE_URL}/accettazione/quota/${idSquadra}?quota=${quota}`, TokenService.getToken());
    },
  },
  torneo: {
    get: async (id) =>{
      return get(`${BASE_URL}/torneo/${id}`);
    },
    // `codice` e' il codice di modifica di chi lavora in accettazione: senza, il server risponde
    // come sempre, cioe' 409 a edizione avviata.
    dettagliIscrizione: async (id, codice = null) => {
      return get(withAdmin(`${BASE_URL}/torneo/iscrizioni/${id}`, codice));
    },

    getAll: async () => {
      return get(`${BASE_URL}/torneo`, TokenService.getToken());
    },

    // Crea una nuova edizione di torneo
    create: async (torneoData) => {
      return post(`${BASE_URL}/torneo`, TokenService.getToken(), torneoData);
    },

    // Aggiorna un'edizione esistente
    update: async (torneoData) => {
      return put(`${BASE_URL}/torneo`, TokenService.getToken(), torneoData);
    },

    // Elimina un'edizione
    delete: async (id) => {
      return del(`${BASE_URL}/torneo/${id}`, TokenService.getToken());
    },

    // Squadre di un torneo specifico
    squadre: async (torneoId) => {
      return get(`${BASE_URL}/torneo/${torneoId}/squadre`);
    },
    dettaglio: async (torneoId) => {
      return get(`${BASE_URL}/torneo/${torneoId}/dettaglio`, TokenService.getToken());
    },
    // transizioni di stato del torneo (avvio/chiusura)
    avvia: async (torneoId) => {
      return post(`${BASE_URL}/torneo/${torneoId}/avvia`, TokenService.getToken());
    },
    chiudi: async (torneoId) => {
      return post(`${BASE_URL}/torneo/${torneoId}/chiudi`, TokenService.getToken());
    },
    annullaAvvio: async (torneoId) => {
      return post(`${BASE_URL}/torneo/${torneoId}/annulla-avvio`, TokenService.getToken());
    },
    annullaChiusura: async (torneoId) => {
      return post(`${BASE_URL}/torneo/${torneoId}/annulla-chiusura`, TokenService.getToken());
    },
    getFormula: async (torneoId) => {
      return get(`${BASE_URL}/torneo/${torneoId}/formula`);
    },
    setFormula: async (torneoId, formulaValue) => {
      return post(`${BASE_URL}/torneo/${torneoId}/formula`, TokenService.getToken(), { Value: formulaValue });
    },
    // Senza il ramo di debug: quello non riportava lo status, e i messaggi di validazione del server (un array) arrivavano come testo grezzo invece che uniti da request.
    crea: async data => {
      return post(`${BASE_URL}/torneo`, TokenService.getToken(), data);
    }
  },
  iscrizioni: {
    disponibili: async () => {
      return get(`${BASE_URL}/torneo/iscrizioni`);
    },
    // Crea una nuova iscrizione
    crea: async (iscrizioneData) => {
      return post(`${BASE_URL}/squadra`, null, iscrizioneData);
    },
    aggiorna: async (iscrizioneData, codice = null) => {
      return put(withAdmin(`${BASE_URL}/squadra`, codice), null, iscrizioneData);
    },
    cancella: async id => del(`${BASE_URL}/squadra/${id}`, TokenService.getToken()),
    byHash: async hash => get(`${BASE_URL}/squadra/modifica/${hash}`),
    modifica: async data => post(`${BASE_URL}/squadra/modifica`, TokenService.getToken(), data),
  },
  giocatore: {
    getByCodice: async (codice) => {
      return get(`${BASE_URL}/giocatore/codice/${codice}`);
    }
  },
  gruppo: {
    getAll: async () => {
      return get(`${BASE_URL}/gruppo`);
    },
    create: async (data) => {
      return post(`${BASE_URL}/gruppo`, TokenService.getToken(), data);
    },
    delete: async (id) => {
      return del(`${BASE_URL}/gruppo/${id}`, TokenService.getToken());
    },
  },
  // La squadra nel tempo: l'anagrafica con cui le statistiche cross-torneo aggregano le iscrizioni
  // di edizioni diverse. Una sola scrittura, "assegna", copre sia l'aggancio sia lo sgancio.
  squadraStorica: {
    getAll: async () => {
      return get(`${BASE_URL}/squadrastorica`, TokenService.getToken());
    },
    iscrizioni: async (idTorneo) => {
      return get(`${BASE_URL}/squadrastorica/iscrizioni/${idTorneo}`, TokenService.getToken());
    },
    create: async (data) => {
      return post(`${BASE_URL}/squadrastorica`, TokenService.getToken(), data);
    },
    update: async (id, data) => {
      return put(`${BASE_URL}/squadrastorica/${id}`, TokenService.getToken(), data);
    },
    delete: async (id) => {
      return del(`${BASE_URL}/squadrastorica/${id}`, TokenService.getToken());
    },
    assegna: async (idSquadra, idStorica) => {
      return put(`${BASE_URL}/squadrastorica/squadra/${idSquadra}`, TokenService.getToken(), { idStorica });
    },
    semina: async () => {
      return post(`${BASE_URL}/squadrastorica/semina`, TokenService.getToken(), {});
    },
  },
  // Chi sono e cosa posso fare. Il backend risolve i permessi a ogni richiesta, quindi questi
  // dati sono sempre freschi e non vanno letti dal token.
  me: {
    profilo: async () => {
      return get(`${BASE_URL}/me`);
    },
    // ambiti amministrabili piu' i nomi di risorse e livelli: le tendine della matrice non si
    // ricopiano gli enum del backend
    ambiti: async () => {
      return get(`${BASE_URL}/me/ambiti`);
    },
  },
  utenti: {
    elenco: async () => {
      return get(`${BASE_URL}/utente`);
    },
    // psw va mandata come MD5, come fa la pagina di login
    crea: async (dati) => {
      return post(`${BASE_URL}/utente`, null, dati);
    },
    aggiorna: async (id, dati) => {
      return put(`${BASE_URL}/utente/${id}`, null, dati);
    },
    cancella: async (id) => {
      return del(`${BASE_URL}/utente/${id}`);
    },
    impostaGruppi: async (id, ids) => {
      return put(`${BASE_URL}/utente/${id}/gruppi`, null, { ids });
    },
    // tutti=true chiede la matrice di ogni ambito amministrabile: e' quello che disegna la griglia
    permessi: async (id, scopeTipo, scopeId, tutti = false) => {
      const parti = [];
      if (scopeTipo) parti.push(`scopeTipo=${scopeTipo}`);
      if (scopeTipo && scopeId) parti.push(`scopeId=${scopeId}`);
      if (tutti) parti.push('tutti=true');
      const q = parti.length ? `?${parti.join('&')}` : '';
      return get(`${BASE_URL}/utente/${id}/permessi${q}`);
    },
    scriviPermessi: async (id, permessi) => {
      return put(`${BASE_URL}/utente/${id}/permessi`, null, { permessi });
    },
  },
  approvazioni: {
    // stato: PENDING, APPROVED, REJECTED, oppure niente per tutte
    elenco: async (stato) => {
      return get(`${BASE_URL}/approvazione${stato ? `?stato=${stato}` : ''}`);
    },
    // quante ne puo' decidere chi chiama: per il badge
    count: async () => {
      return get(`${BASE_URL}/approvazione/count`);
    },
    approva: async (id) => {
      return post(`${BASE_URL}/approvazione/${id}/approva`, null, {});
    },
    rifiuta: async (id, note) => {
      return post(`${BASE_URL}/approvazione/${id}/rifiuta`, null, { note });
    },
  },
  gruppiUtenti: {
    elenco: async () => {
      return get(`${BASE_URL}/gruppo-utenti`);
    },
    crea: async (dati) => {
      return post(`${BASE_URL}/gruppo-utenti`, null, dati);
    },
    aggiorna: async (id, dati) => {
      return put(`${BASE_URL}/gruppo-utenti/${id}`, null, dati);
    },
    cancella: async (id) => {
      return del(`${BASE_URL}/gruppo-utenti/${id}`);
    },
    impostaMembri: async (id, ids) => {
      return put(`${BASE_URL}/gruppo-utenti/${id}/membri`, null, { ids });
    },
    permessi: async (id, scopeTipo, scopeId, tutti = false) => {
      const parti = [];
      if (scopeTipo) parti.push(`scopeTipo=${scopeTipo}`);
      if (scopeTipo && scopeId) parti.push(`scopeId=${scopeId}`);
      if (tutti) parti.push('tutti=true');
      const q = parti.length ? `?${parti.join('&')}` : '';
      return get(`${BASE_URL}/gruppo-utenti/${id}/permessi${q}`);
    },
    scriviPermessi: async (id, permessi) => {
      return put(`${BASE_URL}/gruppo-utenti/${id}/permessi`, null, { permessi });
    },
  },
  squadra: {
    // Ottieni dati squadra tramite hash
    getByHash: async (hash, codice = null) => {
      return get(withAdmin(`${BASE_URL}/squadra/hash/${hash}`, codice));
    },
    getByTorneo: async (torneoId) => {
      return get(`${BASE_URL}/squadra/torneo/${torneoId}`);
    },
    // Non e' una chiamata: e' l'indirizzo da mettere in un <img>. Il logo esce dall'endpoint come
    // immagine, cosi' non pesa sui JSON e il browser lo tiene in cache. Con larghezza si chiede
    // una miniatura di quei pixel invece del file pieno.
    logoUrl: (idSquadra, larghezza) =>
      `${BASE_URL}/squadra/${idSquadra}/logo${larghezza ? `?w=${larghezza}` : ''}`,
  },
  foto: {
    disponibili: async () => {
      return get(`${BASE_URL}/fotoalbum/full`);
    },
    edizione: async id => {
      return get(`${BASE_URL}/fotoalbum/torneo/${id}`);
    },
    uploadIsActive: async (idAlbum) => {
      return get(`${BASE_URL}/fotoalbum/${idAlbum}/upload-active`);
    },
    voteIsActive: async (idAlbum) => {
      return get(`${BASE_URL}/fotoalbum/${idAlbum}/vote-active`);
    },
    upload: async idAlbum => {
      return `${BASE_URL}/foto/upload/${idAlbum}`
    },
    pending: async idAlbum => {
      return get(`${BASE_URL}/foto/album/${idAlbum}/pending`, TokenService.getToken());
    },
    approve: async idsFoto => {
      return put(`${BASE_URL}/foto/approve`, TokenService.getToken(), idsFoto);
    },
    reject: async idsFoto => {
      return put(`${BASE_URL}/foto/reject`, TokenService.getToken(), idsFoto);
    },
    approved: async idAlbum => {
      return get(`${BASE_URL}/foto/album/${idAlbum}/approved`, TokenService.getToken());
    },
    createAlbum: async idTorneo => {
      return post(`${BASE_URL}/fotoalbum/torneo/${idTorneo}`, TokenService.getToken());
    },
    updateAlbum: async album => {
      return put(`${BASE_URL}/fotoalbum`, TokenService.getToken(), album);
    }
  },
  statistiche: {
    // Riassunto completo statistiche torneo
    full: async (torneoId) => {
      return get(`${BASE_URL}/statistiche/${torneoId}`, TokenService.getToken());
    },
    tornei: async () => {
      return get(`${BASE_URL}/statistiche/tornei`, TokenService.getToken());
    },
    // Le stesse statistiche senza il filtro sulla chiusura: la usa il QR di campo, che serve
    // proprio mentre il torneo e' in corso. L'archivio pubblico passa da full().
    live: async (torneoId) => {
      return get(`${BASE_URL}/statistiche/${torneoId}/live`);
    },
  },
  // Le statistiche lette attraverso le edizioni. Tutti anonimi: nessun token.
  carriera: {
    giocatore: async (id) => {
      return get(`${BASE_URL}/carriera/giocatore/${id}`);
    },
    squadra: async (slug) => {
      return get(`${BASE_URL}/carriera/squadra/${encodeURIComponent(slug)}`);
    },
    squadre: async () => {
      return get(`${BASE_URL}/carriera/squadre`);
    },
    giocatori: async ({ page = 1, size = 50, q = '', sesso = '' } = {}) => {
      const params = new URLSearchParams({ page, size });
      if (q) params.set('q', q);
      if (sesso) params.set('sesso', sesso);
      return get(`${BASE_URL}/carriera/giocatori?${params}`);
    },
    cerca: async (q) => {
      return get(`${BASE_URL}/carriera/cerca?q=${encodeURIComponent(q)}`);
    },
    // Le nove classifiche di sempre: sei personali (mete, pieni, assist per sesso) e tre di
    // squadra. Calcolarle lato client vorrebbe dire scaricare l'archivio intero.
    classifiche: async () => {
      return get(`${BASE_URL}/carriera/classifiche`);
    },
    record: async () => {
      return get(`${BASE_URL}/carriera/record`);
    },
  },
  fanta: {
    classifica: async (torneoId) => {
      return get(`${BASE_URL}/statistiche/${torneoId}/fanta`, TokenService.getToken());
    },
    formula: async () => {
      return get(`${BASE_URL}/statistiche/fanta/formula`, TokenService.getToken());
    },
  },
  planning: {
    create: async idTorneo => {
      return get(`${BASE_URL}/planning/${idTorneo}`, TokenService.getToken());
    },
    public: async idTorneo => {
      return get(`${BASE_URL}/planning/${idTorneo}`, TokenService.getToken());
    },
    modifica: async (modificaData) => {
      return post(`${BASE_URL}/planning/modifica`, TokenService.getToken(), modificaData);
    },
    classificaGironi: async idTorneo => {
      return get(`${BASE_URL}/planning/classifica/${idTorneo}/gironi`, TokenService.getToken());
    },
    classificaGenerale: async idEdizione => {
      return get(`${BASE_URL}/planning/classifica/${idEdizione}/generale`, TokenService.getToken());
    },
    calcolaFase: async (idTorneo, fase) => {
      return post(`${BASE_URL}/planning/fase/${idTorneo}`, TokenService.getToken(), fase);
    },
    // Confini delle bande delle fasi nell'header del planning: chi non compare usa il primo
    // turno in cui la fase ha una partita.
    confiniFasi: async idTorneo => {
      return get(`${BASE_URL}/planning/${idTorneo}/fasi`, TokenService.getToken());
    },
    // primoTurno null riporta la fase al confine calcolato.
    impostaConfineFase: async (idTorneo, fase, primoTurno) => {
      return post(`${BASE_URL}/planning/${idTorneo}/fasi`, TokenService.getToken(), { fase, primoTurno });
    },
  },
  partite: {
    // Get a specific match by ID
    get: async (id) => {
      return get(`${BASE_URL}/partite/${id}`, TokenService.getToken());
    },
    // Get all matches
    getAll: async () => {
      return get(`${BASE_URL}/partite`, TokenService.getToken());
    },
    // Create a new match
    create: async (partitaData) => {
      return post(`${BASE_URL}/partite`, TokenService.getToken(), partitaData);
    },
    // Update an existing match
    update: async (partitaData) => {
      return put(`${BASE_URL}/partite`, TokenService.getToken(), partitaData);
    },
    // Delete a match
    delete: async (id) => {
      return del(`${BASE_URL}/partite/${id}`, TokenService.getToken());
    },
    // Le due distinte piu' il punteggio, in una chiamata: e' quello che serve alla pagina di
    // inserimento risultati. Prima si scaricavano tutte le squadre dell'edizione coi loghi.
    editor: async (s1Id, s2Id, tipo = null, nome = null) => {
      let url = `${BASE_URL}/partite/editor?s1=${s1Id}&s2=${s2Id}`;
      if (tipo) {
        url += `&tipo=${encodeURIComponent(tipo)}`;
      }
      if (nome) {
        url += `&nome=${encodeURIComponent(nome)}`;
      }
      return get(url, TokenService.getToken());
    },
    squadre: async (s1Id, s2Id, tipo = null, nome = null) => {
      let url = `${BASE_URL}/partite/squadre?s1=${s1Id}&s2=${s2Id}`;
      if (tipo) {
        url += `&tipo=${encodeURIComponent(tipo)}`;
      }
      if (nome) {
        url += `&nome=${encodeURIComponent(nome)}`;
      }
      return get(url, TokenService.getToken());
    },
    hash: async (s1hash, s2hash, nome = null) => {
      let url = `${BASE_URL}/partite/hash?s1=${s1hash}&s2=${s2hash}`;
      if (nome) {
        url += `&nome=${encodeURIComponent(nome)}`;
      }
      return get(url, TokenService.getToken());
    },
    // Validate hash and get teams data for referto tablet
    referto: async (s1Id, s2Id, hash) => {
      return get(`${BASE_URL}/partite/referto?s1=${s1Id}&s2=${s2Id}&hash=${hash}`);
    },
  },
};

export default API;